import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, DeleteIcon } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';

import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import messages from './tagSettingsMessages';

class RelationGroupOverview extends Component {
  state = {
    groups: null,
    newGroup: null,
    groupToDelete: null,
  };

  componentDidMount() {
    api.base.get(`${config.appUrls.api}/api/RelationGroup/overview`).then(({ data: groups }) => {
      this.setState({
        groups,
      });
    });
  }

  addGroup = () => {
    const { newGroup, groups } = this.state;
    const { intl } = this.props;

    if (!newGroup) return;

    if (groups.find(x => x.name.toLowerCase() === newGroup.toLowerCase())) {
      ToastrService.error(intl.formatMessage(messages.RelationGroupAlreadyExists));
      return;
    }

    api.base
      .post(`${config.appUrls.api}/api/RelationGroup`, { name: newGroup })
      .then(({ data }) => {
        data.nrOfRelationsInUse = 0;
        if (groups.find(x => x.id === data.id)) {
          // Group already exists
          this.setState({
            newGroup: null,
          });
        } else {
          this.setState(state => ({
            groups: [...state.groups, data],
            newGroup: null,
          }));
        }
      });
  };

  editGroupName = (group, value) =>
    api.base
      .put(`${config.appUrls.api}/api/RelationGroup`, { ...group, name: value })
      .then(({ data }) => {
        const { name } = data;
        this.setState(state => ({
          groups: state.groups.map(t => {
            if (t.id === group.id) {
              return { ...t, name };
            }
            return t;
          }),
        }));
      });

  deleteGroup = (group, force = false) => {
    if (group.nrOfRelationsInUse === 0 || force) {
      return api.base.delete(`${config.appUrls.api}/api/RelationGroup/${group.id}`).then(() => {
        this.setState(state => ({
          groups: state.groups.filter(t => t.id !== group.id),
          groupToDelete: null,
        }));
      });
    }
    this.setState({ groupToDelete: group });
  };

  render() {
    const { groups, newGroup, groupToDelete } = this.state;
    const { intl, stateService } = this.props;

    const canUpdate = authService.hasPermission({
      functionality: 'Tags',
      privilege: privileges.Update,
    });

    return (
      <>
        {groups ? (
          <>
            <div className="panel-heading">
              <CanCreate on="Tags">
                <div className="panel-buttons">
                  <button
                    type="button"
                    className="btn btn-inverse"
                    onClick={() => this.setState({ newGroup: '' })}
                  >
                    <i className="fa fa-plus" /> <FormattedMessage {...messages.AddRelationGroup} />
                  </button>
                </div>
              </CanCreate>
            </div>
            <div className="panel-body">
              <table className="table table-grid document-tag-grid">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.Name} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.NrOfRelations} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.Actions} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(group => (
                    <tr key={group.id}>
                      <td>
                        <EditableTextbox
                          value={group.name}
                          onChange={value => this.editGroupName(group, value)}
                          maxLength="200"
                          readOnly={!canUpdate}
                        />
                      </td>

                      <td className="text-right">
                        {group.nrOfRelationsInUse === 0 ? (
                          0
                        ) : (
                          <a
                            style={{ display: 'block' }}
                            className="bold"
                            href={stateService.getStateUrl('relations.overview', {
                              json: { relationGroups: [group.id] },
                            })}
                            onClick={() =>
                              stateService.gotoRelationOverview({ relationGroups: [group.id] })
                            }
                          >
                            {group.nrOfRelationsInUse}
                          </a>
                        )}
                      </td>

                      <td className="text-right">
                        <CanDelete on="Tags">
                          <a
                            onClick={() => this.deleteGroup(group)}
                            className="btn btn-xs btn-icon"
                          >
                            <DeleteIcon />
                          </a>
                        </CanDelete>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <OkCancelModal
                show={newGroup !== null}
                title={intl.formatMessage(messages.AddRelationGroup)}
                onClose={() => this.setState({ newGroup: null })}
                onSave={() => this.addGroup()}
              >
                {newGroup !== null && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={intl.formatMessage(messages.Name)}
                    required
                    value={newGroup}
                    onChange={e => this.setState({ newGroup: e.target.value })}
                    onKeyDown={e => {
                      if (e.keyCode === 13) {
                        this.addGroup();
                      } else if (e.keyCode === 27) {
                        this.setState({ newGroup: null });
                      }
                    }}
                    autoFocus
                  />
                )}
              </OkCancelModal>

              <YesNoModal
                show={!!groupToDelete}
                title={intl.formatMessage(messages.DeleteRelationGroup)}
                message={intl.formatMessage(messages.DeleteRelationGroupConfirmationMsg)}
                onNo={() => this.setState({ groupToDelete: null })}
                onSave={() => this.deleteGroup(groupToDelete, true)}
              />
            </div>
          </>
        ) : (
          <LoadingAnimation />
        )}
      </>
    );
  }
}

RelationGroupOverview.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(RelationGroupOverview));
